import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { Play, ListMusic, ShoppingCart, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import CartDrawer from '@/components/CartDrawer';
import AudioPlayer from '@/components/AudioPlayer';
import { toast } from 'sonner';
import { RELEASE_TYPES, releaseLabel } from '@/constants/releaseTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LATEST_LIMIT = 10;
const resolveCover = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

const CollectionsPage = () => {
  const { addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const activeReleaseType = searchParams.get('release_type') || 'all';
  const activeGenre = searchParams.get('genre') || 'all';
  const viewAll = searchParams.get('view') === 'all';

  const [albums, setAlbums] = useState([]);
  const [genres, setGenres] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentAlbumTracks, setCurrentAlbumTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [gRes, aRes] = await Promise.all([
          axios.get(`${API}/genres`),
          axios.get(`${API}/albums`),
        ]);
        setGenres(gRes.data);
        setAlbums(aRes.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Whether the user has narrowed the view (either type, genre or search)
  const isFiltered = activeReleaseType !== 'all' || activeGenre !== 'all' || Boolean(searchQuery);

  const filteredAlbums = useMemo(() => {
    return albums
      .filter((a) => {
        if (activeReleaseType !== 'all' && a.release_type !== activeReleaseType) return false;
        if (activeGenre !== 'all' && !(a.genre_ids || []).includes(activeGenre)) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const hit =
            a.title.toLowerCase().includes(q) ||
            a.artist.toLowerCase().includes(q) ||
            (a.description || '').toLowerCase().includes(q) ||
            (a.tracks || []).some((t) =>
              t.title.toLowerCase().includes(q) ||
              (t.version || '').toLowerCase().includes(q) ||
              (t.key || '').toLowerCase().includes(q)
            );
          if (!hit) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [albums, activeReleaseType, activeGenre, searchQuery]);

  const displayedAlbums = isFiltered || viewAll ? filteredAlbums : filteredAlbums.slice(0, LATEST_LIMIT);
  const hasMoreThanLatest = filteredAlbums.length > LATEST_LIMIT;

  const showAll = () => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'all');
    setSearchParams(next, { replace: true });
  };

  const setFilterParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all' || !value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => setSearchParams({});

  const handlePlayAlbum = (album, trackIndex = 0) => {
    const list = album.tracks.map((t) => ({
      ...t,
      album_cover: resolveCover(album.cover_url),
      artist: album.artist,
    }));
    setCurrentAlbumTracks(list);
    setCurrentTrackIndex(trackIndex);
    setCurrentTrack(list[trackIndex]);
  };
  const handleNext = () => {
    if (currentTrackIndex < currentAlbumTracks.length - 1) {
      const i = currentTrackIndex + 1;
      setCurrentTrackIndex(i);
      setCurrentTrack(currentAlbumTracks[i]);
    }
  };
  const handlePrevious = () => {
    if (currentTrackIndex > 0) {
      const i = currentTrackIndex - 1;
      setCurrentTrackIndex(i);
      setCurrentTrack(currentAlbumTracks[i]);
    }
  };
  const handleAddToCart = (album) => {
    if (addToCart(album)) toast.success(`${album.title} added to cart`);
    else toast.info('Item already in cart');
  };

  const sectionTitle = (() => {
    if (searchQuery) return `Results for "${searchQuery}"`;
    if (activeReleaseType !== 'all' && activeGenre !== 'all') {
      return `${releaseLabel(activeReleaseType)} · ${genres.find((g) => g.id === activeGenre)?.name || 'Genre'}`;
    }
    if (activeReleaseType !== 'all') return releaseLabel(activeReleaseType);
    if (activeGenre !== 'all') return genres.find((g) => g.id === activeGenre)?.name || 'Genre';
    if (viewAll) return 'All Releases';
    return 'Latest Release';
  })();

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B] text-white">
      <SiteHeader onCartClick={() => setCartOpen(true)} />

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8 sm:py-10 pb-40">
        {/* Page title */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-medium tracking-tight mb-2" data-testid="collections-title">Collections</h1>
          <p className="text-[#A1A1AA] text-sm">Browse every release in the catalog</p>
        </div>

        {/* Release Type Filter */}
        <div className="mb-6">
          <h3 className="text-xs font-medium text-[#A1A1AA] mb-3 uppercase tracking-[0.2em]">Release Type</h3>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilterParam('release_type', 'all')}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${activeReleaseType === 'all' ? 'bg-[#00F0FF] text-black' : 'border border-[#27272A] text-white hover:bg-[#18181B]'}`}
              data-testid="filter-type-all"
            >
              All
            </button>
            {RELEASE_TYPES.map((rt) => (
              <button
                key={rt.value}
                onClick={() => setFilterParam('release_type', rt.value)}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${activeReleaseType === rt.value ? 'bg-[#00F0FF] text-black' : 'border border-[#27272A] text-white hover:bg-[#18181B]'}`}
                data-testid={`filter-type-${rt.value}`}
              >
                {rt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Genre Filter */}
        {genres.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xs font-medium text-[#A1A1AA] mb-3 uppercase tracking-[0.2em]">Genre</h3>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterParam('genre', 'all')}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${activeGenre === 'all' ? 'bg-[#00F0FF] text-black' : 'border border-[#27272A] text-white hover:bg-[#18181B]'}`}
                data-testid="filter-genre-all"
              >
                All Genres
              </button>
              {genres.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setFilterParam('genre', g.id)}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${activeGenre === g.id ? 'bg-[#00F0FF] text-black' : 'border border-[#27272A] text-white hover:bg-[#18181B]'}`}
                  data-testid={`filter-genre-${g.slug}`}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 mb-6" data-testid="section-header">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-medium tracking-tight" data-testid="section-title">
            {sectionTitle}
          </h2>
          <div className="flex items-center flex-wrap gap-3">
            {!isFiltered && !viewAll && (
              <>
                {hasMoreThanLatest && (
                  <span className="text-xs text-[#A1A1AA] hidden sm:inline">
                    Showing 10 latest · {filteredAlbums.length} total
                  </span>
                )}
                {filteredAlbums.length > 0 && (
                  <button
                    onClick={showAll}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium border border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors"
                    data-testid="view-all-button"
                  >
                    View all {filteredAlbums.length}
                  </button>
                )}
              </>
            )}
            {(isFiltered || viewAll) && (
              <button
                onClick={clearAll}
                className="text-xs text-[#A1A1AA] hover:text-white underline underline-offset-2"
                data-testid="clear-filters-button"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Album grid */}
        {displayedAlbums.length === 0 ? (
          <div className="text-center py-24">
            <ListMusic className="mx-auto mb-4 text-[#27272A]" size={64} />
            <p className="text-[#A1A1AA]">No releases match your filters</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeReleaseType}-${activeGenre}-${viewAll ? 'all' : 'latest'}-${searchQuery}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-6"
              data-testid="albums-grid"
            >
              {displayedAlbums.map((album, i) => (
                <motion.div
                  key={album.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.4), duration: 0.28, ease: 'easeOut' }}
                  className="group"
                  data-testid={`album-card-${album.id}`}
                >
                <Link
                  to={`/release/${album.id}`}
                  className="relative block mb-4 rounded-lg overflow-hidden bg-[#121214] transition-transform duration-300 group-hover:scale-[1.02]"
                  data-testid={`album-cover-link-${album.id}`}
                >
                  <img src={resolveCover(album.cover_url)} alt={album.title} className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePlayAlbum(album); }}
                    className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-[#00F0FF] hover:bg-[#33F3FF] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:scale-110 shadow-lg shadow-black/40"
                    data-testid={`play-album-${album.id}`}
                    aria-label={`Play ${album.title}`}
                  >
                    <Play size={18} fill="#000" stroke="#000" className="ml-0.5" />
                  </button>
                  {album.release_type && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white uppercase tracking-wider">
                      {releaseLabel(album.release_type)}
                    </span>
                  )}
                </Link>
                <div className="space-y-1">
                  <Link
                    to={`/release/${album.id}`}
                    className="block font-heading font-medium truncate hover:text-[#00F0FF] transition-colors"
                    data-testid={`album-title-link-${album.id}`}
                  >
                    {album.title}
                  </Link>
                  <p className="text-sm text-[#A1A1AA] truncate">{album.artist}</p>
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-[#00F0FF] font-medium">
                      {album.release_type === 'free-download' ? 'Free' : `₱${album.price.toFixed(2)}`}
                    </span>
                    {album.release_type === 'free-download' ? (
                      <Link
                        to={`/release/${album.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 bg-[#18181B] hover:bg-[#00F0FF] hover:text-black border border-[#27272A] rounded-full transition-colors"
                        data-testid={`free-download-${album.id}`}
                        aria-label={`Free download ${album.title}`}
                      >
                        <Download size={16} />
                      </Link>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(album)}
                        className="bg-[#18181B] hover:bg-[#00F0FF] hover:text-black border border-[#27272A] rounded-full"
                        data-testid={`add-to-cart-${album.id}`}
                      >
                        <ShoppingCart size={16} />
                      </Button>
                    )}
                  </div>
                </div>
                </motion.div>
            ))}
            </motion.div>
          </AnimatePresence>
        )}
      </main>

      <SiteFooter />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <AudioPlayer
        currentTrack={currentTrack}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onClose={() => setCurrentTrack(null)}
      />
    </div>
  );
};

export default CollectionsPage;
